import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // ✅ Ավելացված է
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { API_BASE } from './apiConfig';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Portfolio = () => {
    const { t } = useTranslation(); // ✅ i18n հուկ
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    const goldColor = '#c5a059';
    const darkBlue = '#0f172a';
    const PLACEHOLDER_IMG = "https://placehold.co/600x400/0f172a/c5a059?text=Liber+Group";

    useEffect(() => {
        window.scrollTo(0, 0);
        fetch(`${API_BASE}/api/portfolio`, {
            headers: { 'ngrok-skip-browser-warning': 'true' }
        })
            .then(res => res.json())
            .then(data => {
                setProjects(data || []);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching portfolio:", err);
                setLoading(false);
            });
    }, []);

    const getFullImagePath = (img) => {
        if (!img) return PLACEHOLDER_IMG;
        if (img.startsWith('http')) return img;
        const path = img.startsWith('/') ? img : `/uploads/${img}`;
        return `${API_BASE}${path}`;
    };

    const handleImgError = (e) => {
        if (e.target.src !== PLACEHOLDER_IMG) {
            e.target.src = PLACEHOLDER_IMG;
        }
    };

    if (loading) {
        return <div style={loaderStyle}>{t('loading')}...</div>;
    }

    return (
        <div style={pageStyle}>
            <style>
                {`
                .portfolio-swiper .swiper-button-next, 
                .portfolio-swiper .swiper-button-prev { 
                    width: 35px; height: 35px; background: rgba(255,255,255,0.1); 
                    backdrop-filter: blur(5px); border-radius: 50%; opacity: 0; transition: 0.3s; 
                }
                .portfolio-card:hover .swiper-button-next, 
                .portfolio-card:hover .swiper-button-prev { opacity: 1; }
                .portfolio-swiper .swiper-button-next:after, 
                .portfolio-swiper .swiper-button-prev:after { font-size: 14px !important; color: #fff; font-weight: bold; }
                .portfolio-swiper .swiper-pagination-bullet { background: #fff !important; opacity: 0.5; }
                .portfolio-swiper .swiper-pagination-bullet-active { background: ${goldColor} !important; opacity: 1; }
                .portfolio-card:hover .hover-overlay { opacity: 1 !important; transform: translateY(0) !important; }
                `}
            </style>

            <div style={headerSection}>
                <h1 style={mainTitle}>
                    {t('portfolio_title_prefix')} <span style={{ color: goldColor }}>{t('portfolio_title_main')}</span>
                </h1>
                <p style={subTitle}>{t('portfolio_subtitle')}</p>
                <div style={divider}></div>
            </div>

            <div style={gridStyle}>
                {projects.map((item) => {
                    const images = item.image ? (typeof item.image === 'string' ? item.image.split(',') : [item.image]) : [];

                    return (
                        <div key={item.id} className="portfolio-card" style={cardStyle}>
                            
                            <div style={imgContainerStyle}>
                                
                                <Swiper
                                    className="portfolio-swiper"
                                    modules={[Navigation, Pagination, Autoplay]}
                                    navigation={true}
                                    pagination={{ clickable: true }}
                                    loop={images.length > 1}
                                    autoplay={{ delay: 4000, disableOnInteraction: false }}
                                    style={{ height: '100%' }}
                                >
                                    {images.length > 0 ? images.map((img, index) => (
                                        <SwiperSlide key={index}>
                                          <img 
    src={getFullImagePath(img.trim())} 
    alt={item.name || "Gallery image"} 
    style={imgStyle}
    loading="lazy"       // 1. Բեռնել միայն երբ օգտատերը սքրոլ անի դեպի այս նկարը
    decoding="async"     // 2. Մշակել ֆոնում, որպեսզի սլայդերը չկախի
    onError={handleImgError} 
/>
                                        </SwiperSlide>
                                    )) : (
                                        <SwiperSlide>
                                            <img src={PLACEHOLDER_IMG} style={imgStyle} alt="no-img" />
                                        </SwiperSlide>
                                    )}
                                </Swiper>
                                
                                <div className="hover-overlay" style={hoverOverlayStyle}>
                                    <div style={{ padding: '30px', textAlign: 'center' }}>
                                        <p style={descriptionStyle}>{item.description}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div style={infoBlockStyle}>
                                <h3 style={projectTitle}>{item.name}</h3>
                                <div style={projectMeta}>
                                    <span style={imageCount}>
                                        <span style={dot}></span> {images.length} {t('portfolio_photo_count')}
                                    </span>
                                    {item.date && <span style={dateStyle}>{item.date}</span>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {projects.length === 0 && (
                <div style={emptyState}>{t('no_projects')}</div>
            )}
        </div>
    );
};

const pageStyle = {
    padding: '160px 8% 100px',
    background: '#f8fafc',
    minHeight: '100vh'
};

const headerSection = {
    textAlign: 'center',
    marginBottom: '60px'
};

const mainTitle = {
    fontSize: '38px',
    fontWeight: '900',
    color: '#0f172a',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
};

const subTitle = {
    color: '#64748b',
    fontSize: '16px',
    maxWidth: '600px',
    margin: '0 auto'
};

const divider = {
    width: '60px',
    height: '4px',
    background: '#c5a059',
    margin: '20px auto',
    borderRadius: '2px'
};

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '35px',
    maxWidth: '1300px',
    margin: '0 auto'
};

const cardStyle = {
    background: '#fff',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 15px 35px rgba(0,0,0,0.05)',
    transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
    border: '1px solid #f1f5f9'
};

const imgContainerStyle = {
    position: 'relative',
    height: '280px',
    overflow: 'hidden'
};

const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
};

const hoverOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(4px)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transform: 'translateY(20px)',
    transition: 'all 0.4s ease',
    zIndex: 5,
    pointerEvents: 'none'
};

const descriptionStyle = {
    fontSize: '14px',
    lineHeight: '1.8',
    fontWeight: '400',
    fontStyle: 'italic',
    color: '#e2e8f0'
};

const infoBlockStyle = {
    padding: '25px'
};

const projectTitle = {
    margin: '0 0 12px 0',
    fontSize: '19px',
    color: '#0f172a',
    fontWeight: '800'
};

const projectMeta = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

const imageCount = {
    color: '#c5a059',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const dot = {
    width: '6px',
    height: '6px',
    background: '#c5a059',
    borderRadius: '50%'
};

const dateStyle = {
    fontSize: '12px',
    color: '#94a3b8',
    fontWeight: '500'
};

const loaderStyle = {
    padding: '200px 0',
    textAlign: 'center',
    color: '#64748b',
    fontSize: '18px',
    fontWeight: '600'
};

const emptyState = {
    textAlign: 'center',
    padding: '100px',
    color: '#94a3b8',
    fontSize: '16px'
};

export default Portfolio;